import { LocationQueryRaw, NavigationFailure, RouteLocationRaw, useRouter } from 'vue-router';

import { AppRouteNames } from './app-routes.enum';
import {
  confirmResetPasswordRoute,
  emailConfirmationRoute,
  missingPasswordSignInRoute,
  newListingRoute,
  oneTimeCodeSignInRoute,
  portfolioRoute,
  profileRoute,
  propertyDetailsRoute,
  propertyRoute,
  propertyDashboardRoute,
  propertyAvailabilityRoute,
  renterProfileRoute,
  resetPasswordRoute,
  showingDetailsRoute,
  signInRoute,
  signUpRoute,
  renterApplicationReviewRoute,
  renterTransUnionReportRoute,
} from './navigation-routes';

type NavigationResult = Promise<NavigationFailure | void | undefined>;

export function useNavigation() {
  const router = useRouter();

  const goBack = (fallbackRoute?: RouteLocationRaw) => {
    const hasPreviousPageToNavigateTo = window.history.state?.back;

    if (hasPreviousPageToNavigateTo) {
      router.back(); // if there is a history, go back
    } else if (fallbackRoute) {
      router.replace(fallbackRoute); // if there is no history (e.g. url opened from the link), go to the fallback route
    } else {
      router.replace('/'); // if there is no history and no fallback route, go to the home page
    }
  };

  const openSignIn = async (): NavigationResult => {
    return router.push(signInRoute());
  };

  const openSignUp = async (): NavigationResult => {
    return router.push(signUpRoute());
  };

  const openOnboarding = async (): NavigationResult => {
    return router.push({ name: AppRouteNames.INVESTOR_ONBOARDING });
  };

  const openEmailConfirmation = async (queryParams?: LocationQueryRaw): NavigationResult => {
    return router.push(emailConfirmationRoute(queryParams));
  };

  const openMissingPasswordSignIn = async (queryParams: { email: string }): NavigationResult => {
    return router.push(missingPasswordSignInRoute(queryParams));
  };

  const openOneTimeCodeSignIn = async (queryParams: { email: string }): NavigationResult => {
    return router.push(oneTimeCodeSignInRoute(queryParams));
  };

  const openPasswordReset = async (queryParams: { email: string; sent: string }): NavigationResult => {
    return router.push(resetPasswordRoute(queryParams));
  };

  const openPasswordResetConfirmation = async (): NavigationResult => {
    return router.push(confirmResetPasswordRoute());
  };

  const openNewPropertyOnboarding = async (): NavigationResult => {
    return router.push(newListingRoute());
  };

  const openShowingDetails = async (showingId: string, replaceRoute = false): NavigationResult => {
    const route = showingDetailsRoute(showingId);

    return replaceRoute ? router.replace(route) : router.push(route);
  };

  const openPortfolio = async (): NavigationResult => {
    return router.push(portfolioRoute());
  };

  const openProfile = async (tab?: string): NavigationResult => {
    return router.push(profileRoute(tab));
  };

  const openProperty = async (propertyId: string): NavigationResult => {
    return router.push(propertyRoute(propertyId));
  };

  const openPropertyDashboard = async (propertyId: string): NavigationResult => {
    return router.push(propertyDashboardRoute(propertyId));
  };

  const openPropertyDetails = async (propertyId: string): NavigationResult => {
    return router.push(propertyDetailsRoute(propertyId));
  };

  const openPropertyAvailability = async (propertyId: string): NavigationResult => {
    return router.push(propertyAvailabilityRoute(propertyId));
  };

  const openRenterProfile = async (propertyId: string, renterId: string): NavigationResult => {
    return router.push(renterProfileRoute(propertyId, renterId));
  };

  const openRenterApplicationReview = async (
    propertyId: string,
    renterId: string,
    applicationId: string,
  ): NavigationResult => {
    return router.push(renterApplicationReviewRoute(propertyId, renterId, applicationId));
  };

  const openRenterTransUnionReportRoute = async (
    applicationId: string,
    reportType: string,
    format?: string,
  ): NavigationResult => {
    return router.push(renterTransUnionReportRoute(applicationId, reportType, format));
  };

  return {
    goBack,

    openShowingDetails,
    openSignIn,
    openSignUp,
    openEmailConfirmation,
    openMissingPasswordSignIn,
    openOneTimeCodeSignIn,
    openPasswordReset,
    openPasswordResetConfirmation,
    openNewPropertyOnboarding,
    openOnboarding,
    openProperty,
    openPropertyDashboard,
    openPropertyDetails,
    openPropertyAvailability,
    openRenterProfile,
    openPortfolio,
    openProfile,
    openRenterApplicationReview,
    openRenterTransUnionReportRoute,
  };
}
